import { HeaderName } from '../constants';
import parseRetryAfterHeader from './parseRetryAfterHeader';

const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_BACKOFF_MULTIPLIER = 1.6;
const DEFAULT_INITIAL_BACKOFF = 100;
const DEFAULT_MAX_BACKOFF = 120_000;

/**
 * A method is retryable is it is considered
 * {@link https://httpwg.org/specs/rfc9110.html#safe.methods|safe} or
 * {@link https://httpwg.org/specs/rfc9110.html#idempotent.methods|idempotent}.
 */
const RETRYABLE_METHODS: readonly string[] = [
  'GET',
  'HEAD',
  'OPTIONS',
  'PUT',
  'DELETE',
];

const ALWAYS_RETRYABLE_STATUSES: readonly number[] = [502, 503];

const RETRYABLE_STATUSES: readonly number[] = [429, 504];

function calculateRetryDelay(
  response: Response,
  attempt: number,
  initialBackoff: number,
  backoffMultiplier: number,
  maxBackoff: number,
): number {
  const retryAfterMs = parseRetryAfterHeader(
    response.headers.get(HeaderName.RetryAfter),
  );

  if (retryAfterMs != null) {
    return retryAfterMs;
  }

  return Math.min(initialBackoff * backoffMultiplier ** attempt, maxBackoff);
}

/**
 * A function that determines whether a request should be retried, and if so,
 * after how many milliseconds.
 *
 * If the function returns `undefined`, the request will not be retried.
 * If it returns a number, the request will be retried after that many milliseconds.
 */
export type RetryStrategy = (
  request: Request,
  response: Response,
  attempt: number,
) => number | undefined;

export interface RetryStrategyOptions {
  /**
   * Additional HTTP request methods that should be considered retryable,
   * in addition to the standard {@link RETRYABLE_METHODS|retryable methods}.
   */
  readonly additionalRetryableMethods?: readonly string[];
  /**
   * Additional HTTP response statuses that should be considered retryable,
   * in addition to the standard {@link RETRYABLE_STATUSES|retryable statuses}.
   */
  readonly additionalRetryableStatuses?: readonly number[];
  readonly maxAttempts?: number;
  readonly initialBackoff?: number;
  readonly backoffMultiplier?: number;
  readonly maxBackoff?: number;
}

export function createRetryStrategy({
  additionalRetryableMethods,
  additionalRetryableStatuses,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  initialBackoff = DEFAULT_INITIAL_BACKOFF,
  backoffMultiplier = DEFAULT_BACKOFF_MULTIPLIER,
  maxBackoff = DEFAULT_MAX_BACKOFF,
}: RetryStrategyOptions = {}): RetryStrategy {
  function shouldRetry(request: Request, response: Response): boolean {
    if (ALWAYS_RETRYABLE_STATUSES.includes(response.status)) {
      return true;
    }

    if (
      (RETRYABLE_METHODS.includes(request.method) ||
        additionalRetryableMethods?.includes(request.method)) &&
      (RETRYABLE_STATUSES.includes(response.status) ||
        additionalRetryableStatuses?.includes(response.status))
    ) {
      return true;
    }

    return false;
  }

  return function retryStrategy(request, response, attempt) {
    if (attempt <= maxAttempts && shouldRetry(request, response)) {
      return calculateRetryDelay(
        response,
        attempt,
        initialBackoff,
        backoffMultiplier,
        maxBackoff,
      );
    }

    return undefined;
  };
}

export const defaultRetryStrategy = createRetryStrategy();
